import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth.jsx";

export default function Leaders() {
    const { user } = useAuth();
    const [leaders, setLeaders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/leaders");
            setLeaders(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!confirm("Slet lederen?")) return;
        try {
            await api.delete(`/leaders/${id}`);
            toast.success("Leder slettet");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke slette");
        }
    };

    const startNew = () => { setEditing({ name: "", email: "", notes: "" }); setOpen(true); };
    const startEdit = (l) => { setEditing({ ...l }); setOpen(true); };

    const save = async () => {
        try {
            if (editing.id) {
                await api.patch(`/leaders/${editing.id}`, {
                    name: editing.name,
                    email: editing.email,
                    notes: editing.notes,
                });
                toast.success("Leder opdateret");
            } else {
                await api.post("/leaders", {
                    name: editing.name,
                    email: editing.email,
                    notes: editing.notes,
                });
                toast.success("Leder oprettet");
            }
            setOpen(false);
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke gemme");
        }
    };

    return (
        <div className="space-y-6" data-testid="leaders-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-3xl font-medium text-[#2C2A29]">Ledere</h1>
                    <p className="text-sm text-[#78736E] mt-1">Modtagere af eskalerede sager.</p>
                </div>
                {user?.role !== "medarbejder" && (
                    <Button onClick={startNew} className="bg-[#4A4641] hover:bg-[#2C2A29] text-white" data-testid="new-leader-button">
                        <Plus className="w-4 h-4 mr-2" />
                        Ny leder
                    </Button>
                )}
            </div>

            <div className="bg-white border border-[#E5E1DA] rounded-xl shadow-[0_8px_32px_rgba(44,42,41,0.04)] overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="border-[#E5E1DA] bg-[#FAFAF9]">
                            <TableHead>Navn</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Noter</TableHead>
                            <TableHead className="w-24" />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow><TableCell colSpan={4} className="text-center py-12 text-[#78736E]">Indlæser…</TableCell></TableRow>
                        )}
                        {!loading && leaders.length === 0 && (
                            <TableRow><TableCell colSpan={4} className="text-center py-12 text-[#78736E]">Ingen ledere endnu.</TableCell></TableRow>
                        )}
                        {leaders.map((l) => (
                            <TableRow key={l.id} className="border-[#E5E1DA]" data-testid={`leader-row-${l.id}`}>
                                <TableCell className="font-medium">{l.name}</TableCell>
                                <TableCell>{l.email}</TableCell>
                                <TableCell className="text-sm text-[#78736E] truncate max-w-md">{l.notes}</TableCell>
                                <TableCell>
                                    {user?.role !== "medarbejder" && (
                                        <div className="flex gap-1 justify-end">
                                            <Button variant="ghost" size="icon" onClick={() => startEdit(l)} data-testid={`edit-leader-${l.id}`}><Pencil className="w-4 h-4" /></Button>
                                            <Button variant="ghost" size="icon" onClick={() => remove(l.id)} data-testid={`delete-leader-${l.id}`}><Trash2 className="w-4 h-4 text-[#993C3C]" /></Button>
                                        </div>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-white">
                    <DialogHeader>
                        <DialogTitle>{editing?.id ? "Rediger leder" : "Ny leder"}</DialogTitle>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <div>
                                <Label>Navn</Label>
                                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="leader-name-input" />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="leader-email-input" />
                            </div>
                            <div>
                                <Label>Noter</Label>
                                <Textarea rows={3} value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} data-testid="leader-notes-input" />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Annullér</Button>
                        <Button onClick={save} className="bg-[#4A4641] hover:bg-[#2C2A29] text-white" data-testid="save-leader-button">Gem</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
